import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  EventEmitter,
  Output,
  ViewChild,
  ElementRef,
  AfterViewInit,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import mapboxgl from 'mapbox-gl';

// Services
import { MapaFacilV2Service, MapaFacilV2Config } from './mapa-facil-v2.service';
import { MapboxDrawService } from './mapbox-draw.service';

// Tipos
import { TipoMapaBase } from '../../modulos/standalone/selector-tipo-mapa/selector-tipo-mapa.component';

// Environment
import { MAPBOX_TOKEN } from '../../../environments/environment';

// Components
import { PopupGenericoComponent } from '../mapa-facil/popup-generico/popup-generico.component';
import { SelectorTipoMapaComponent } from '../../modulos/standalone/selector-tipo-mapa/selector-tipo-mapa.component';
import { ILayerMapaPersonalizado } from 'modelos/src';

/**
 * MapaFacilV2Component
 *
 * Componente wrapper para el nuevo sistema de mapas basado 100% en managers.
 * Reemplaza completamente a MapaFacilComponent (legacy).
 *
 * Características:
 * - API simplificada y moderna
 * - Inicialización automática
 * - Limpieza automática de recursos
 * - Compatible con la nueva arquitectura de managers
 * - Sin dependencias a código legacy (OpenLayers, mapa-facil-view)
 *
 * Uso:
 * ```html
 * <app-mapa-facil-v2
 *   [center]="[-58.443832, -34.587386]"
 *   [zoom]="12"
 *   [style]="'osm'"
 *   (mapReady)="onMapReady($event)"
 * ></app-mapa-facil-v2>
 * ```
 *
 * ```typescript
 * onMapReady(map: mapboxgl.Map) {
 *   this.mapaFacilV2.updateData({
 *     activos: this.reportesActivos
 *   });
 * }
 * ```
 */
@Component({
  selector: 'app-mapa-facil-v2',
  standalone: true,
  imports: [CommonModule, PopupGenericoComponent, SelectorTipoMapaComponent],
  template: `
    <div #mapContainer class="map-container"></div>

    <!-- Selector de tipo de mapa (flotante en la parte inferior izquierda) -->
    @if (showMapSelector) {
      <app-selector-tipo-mapa
        [tipoMapaBase]="style"
        [mapasPersonalizados]="mapasPersonalizados"
        [useAbsolutePosition]="true"
        [tipoMapaBaseKey]="mapId ? 'tipoMapaBase_' + mapId : 'tipoMapaBase'"
        (tipoMapaBaseChange)="onMapStyleChange($event)"
      ></app-selector-tipo-mapa>
    }

    <ng-content></ng-content>

    <!-- Popup genérico (mismo que en legacy) -->
    <!-- IMPORTANTE: El contenedor con ID se usa para crear el mapboxgl.Popup -->
    <!-- Usar @if para forzar la recreación del contenedor cuando se cierra el popup -->
    <!-- OPTIMIZACIÓN OnPush: Usar | async para que Angular detecte cambios automáticamente -->
    @if (mapaFacilV2Service.popupVisible$ | async) {
      <div id="mapa-facil-v2-popup-container" style="pointer-events: auto;">
        <app-popup-generico
          [visible]="(mapaFacilV2Service.popupVisible$ | async) ?? false"
          titulo="Información"
          [datos]="mapaFacilV2Service.popupDatos$ | async"
          [tipo]="mapaFacilV2Service.popupTitulo$ | async"
          (cerrar)="onCerrarPopup()"
        ></app-popup-generico>
      </div>
    }
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
        height: 100%;
      }

      .map-container {
        width: 100%;
        height: 100%;
      }
    `,
  ],
})
export class MapaFacilV2Component implements AfterViewInit, OnDestroy {
  mapaFacilV2Service = inject(MapaFacilV2Service);

  // ========== INPUTS ==========
  @Input() center: [number, number] = [-58.443832, -34.587386]; //Centro inicial del mapa
  @Input() zoom: number = 12; //Zoom inicial del mapa
  @Input() pitch: number = 0; //Inclinación inicial
  @Input() bearing: number = 0; //Rotación inicial
  @Input() style: TipoMapaBase = 'osm'; //Estilo capa base inicial
  @Input() enableDrawing: boolean = false; //Habilitración de controles de dibujo/edición
  @Input() mapId?: string; //Identificador único del mapa
  @Input() showMapSelector: boolean = true; //Mostrar selector de tipo de capa base
  @Input() disablePopups: boolean = false; //Deshabilitar popups
  @Input() cooperativeGestures: boolean = false; //Requiere Ctrl/Cmd + scroll para hacer zoom (evita scroll accidental)(útil en mapas de formularios)
  @Input() mapasPersonalizados: ILayerMapaPersonalizado[] = []; //Mapas personalizados adicionales
  @Input() allowedEntityTypes: string[] | null = null; // Entidades permitidas para re-aplicar datos al cambiar capa base (sirve como medida de seguridad para no traer entidades indeseadas a mapas en las que no deben estar)
  @Input() reapplyDataOnStyleChange: boolean = true; //Reaplicar datos que están en el caché a mapas que cambian de estilo
  @Input() includeGeometriesOnStyleChange: boolean = true; //Reaplicar geometrías (polígonos, líneas, etc) al cambiar de estilo

  // ========== OUTPUTS ==========
  @Output() mapReady = new EventEmitter<mapboxgl.Map>(); //Emitido cuando el mapa está completamente cargado e inicializado
  @Output() entityClick = new EventEmitter<any>(); //Emitido cuando se hace click en una entidad del mapa

  // ========== VIEW CHILD ==========

  @ViewChild('mapContainer', { static: true })
  mapContainer!: ElementRef<HTMLDivElement>;

  // ========== PROPIEDADES ==========

  private map: mapboxgl.Map | null = null;
  private isInitialized = false;

  // ========== LIFECYCLE HOOKS ==========

  async ngAfterViewInit(): Promise<void> {
    try {
      mapboxgl.accessToken = MAPBOX_TOKEN; //Configurar token de Mapbox

      // Leer posición y zoom guardados en el localStorage (si existe)
      const savedPosition = this.getSavedMapPosition();
      const center = savedPosition?.center || this.center;
      const zoom = savedPosition?.zoom || this.zoom;

      // Leer tipo de mapa guardado en el localStorage (si existe)
      const savedMapStyle = this.getSavedMapStyle();
      const style = savedMapStyle || this.style;
      if (savedMapStyle) {
        this.style = savedMapStyle; //Actualizar this.style para que el selector se sincronice correctamente
      }
      // Configurar mapas personalizados si existen
      if (this.mapasPersonalizados && this.mapasPersonalizados.length > 0) {
        this.mapaFacilV2Service.setMapasPersonalizados(
          this.mapasPersonalizados,
        );
      }

      // Configurar el alcance de re-aplicación de datos cuando cambie la capa base
      this.mapaFacilV2Service.setAllowedEntityTypes(
        this.allowedEntityTypes ?? null,
      );
      this.mapaFacilV2Service.setReapplyDataOnStyleChange(
        this.reapplyDataOnStyleChange,
      );
      this.mapaFacilV2Service.setIncludeGeometriesOnStyleChange(
        this.includeGeometriesOnStyleChange,
      );

      // Configuración del mapa
      const config: MapaFacilV2Config = {
        container: this.mapContainer.nativeElement,
        center: center,
        zoom: zoom,
        pitch: this.pitch,
        bearing: this.bearing,
        style: style,
        disablePopups: this.disablePopups,
        cooperativeGestures: this.cooperativeGestures,
        mapId: this.mapId,
      };

      //Crear mapa
      this.map = await this.mapaFacilV2Service.createMap(config);

      // Guardar posición del mapa cuando el usuario lo mueva
      this.setupPositionSaving();

      // Habilitar drawing si se solicitó
      if (this.enableDrawing) {
        this.mapaFacilV2Service.enableDrawing();
      }

      // Registrar eventos de click en las capas
      this.setupClickHandlers();

      this.isInitialized = true;

      // Emitir evento de mapa listo
      this.mapReady.emit(this.map);
    } catch (error) {
      console.error('❌ Error inicializando mapa:', error);
    }
  }

  ngOnDestroy(): void {
    // Limpiar el mapa
    this.mapaFacilV2Service.destroy();

    // Resetear configuración de re-aplicación para no afectar otros mapas
    this.mapaFacilV2Service.setAllowedEntityTypes(null);
    this.mapaFacilV2Service.setReapplyDataOnStyleChange(true);
    this.mapaFacilV2Service.setIncludeGeometriesOnStyleChange(true);

    this.map = null;
    this.isInitialized = false;
  }

  // ========== PUBLIC API ==========
  getMap(): mapboxgl.Map | null {
    return this.map;
  } //Obtiene la instancia del mapa Mapbox

  //Verifica si el mapa está listo
  isMapReady(): boolean {
    return this.isInitialized;
  }

  //Cambia el estilo base del mapa
  changeStyle(style: TipoMapaBase): void {
    this.mapaFacilV2Service.setMapStyle(style);
  }

  //Centra el mapa en una coordenada (opcionalmente con zoom)
  centerMap(center: [number, number], zoom?: number): void {
    if (!this.map) return;

    this.map.flyTo({
      center,
      zoom: zoom || this.map.getZoom(),
    });
  }

  //Ajusta el zoom del mapa para mostrar todas las entidades
  fitBounds(coordinates: [number, number][]): void {
    if (!this.map || coordinates.length === 0) return;

    const bounds = new mapboxgl.LngLatBounds();

    coordinates.forEach((coord) => {
      bounds.extend(coord);
    });

    this.map.fitBounds(bounds, {
      padding: 50,
      maxZoom: 15,
    });
  }

  // ========== PERSISTENCIA DE POSICIÓN Y ESTILO ==========

  //Lee la posición guardada del mapa en el localStorage
  private getSavedMapPosition(): {
    center: [number, number];
    zoom: number;
  } | null {
    if (!this.mapId) return null;

    try {
      const savedLng = localStorage.getItem(`mapLng${this.mapId}`);
      const savedLat = localStorage.getItem(`mapLat${this.mapId}`);
      const savedZoom = localStorage.getItem(`mapZoom${this.mapId}`);

      if (savedLng && savedLat && savedZoom) {
        return {
          center: [parseFloat(savedLng), parseFloat(savedLat)],
          zoom: parseFloat(savedZoom),
        };
      }
    } catch (error) {
      console.warn('⚠️ Error leyendo posición guardada del mapa:', error);
    }

    return null;
  }

  //Lee el tipo de mapa guardado del localStorage
  //Usa el mismo key que SelectorTipoMapaComponent para consistencia

  private getSavedMapStyle(): TipoMapaBase | null {
    try {
      // Usar el mismo key que SelectorTipoMapaComponent
      const key = this.mapId ? `tipoMapaBase_${this.mapId}` : 'tipoMapaBase';
      const savedStyle = localStorage.getItem(key);

      if (savedStyle) {
        return savedStyle as TipoMapaBase;
      }
    } catch (error) {
      console.warn('⚠️ Error leyendo tipo de mapa guardado:', error);
    }

    return null;
  }

  //Guardado automático de la posición del mapa
  private setupPositionSaving(): void {
    if (!this.map || !this.mapId) return;

    // Guardar posición cuando el mapa se mueve/hace zoom
    this.map.on('moveend', () => {
      if (!this.map || !this.mapId) return;

      const center = this.map.getCenter();
      const zoom = this.map.getZoom();

      localStorage.setItem(`mapLng${this.mapId}`, center.lng.toString());
      localStorage.setItem(`mapLat${this.mapId}`, center.lat.toString());
      localStorage.setItem(`mapZoom${this.mapId}`, zoom.toString());
    });
  }

  // ========== POPUP HANDLERS ==========

  //Cerrar el popup
  onCerrarPopup(): void {
    this.mapaFacilV2Service.cerrarPopup();
  }

  // ========== CLICK HANDLERS ==========

  /**
   * Configura los handlers de click para las diferentes capas del mapa
   * Emite el evento entityClick cuando se hace click en una entidad
   */
  private setupClickHandlers(): void {
    if (!this.map) return;

    const clickableLayers = [
      'vehiculos-layer',
      'alarmas-layer',
      'alarmas-circle-layer',
      'luminarias-icon-layer',
      'luminarias-circle-layer',
      'luminarias-unclustered-layer',
      'gateways-layer',
      'sirenas-layer',
      'ambulancias-layer',
      'hospitales-layer',
    ];

    const getEntityType = (layerId: string) => {
      if (layerId.startsWith('luminarias-')) return 'luminarias';
      if (layerId.startsWith('alarmas-')) return 'alarmas';
      return layerId.replace(/-layer$/, '');
    };

    let clickHandled = false;

    clickableLayers.forEach((layerId) => {
      if (this.map?.getLayer(layerId)) {
        this.map.on('click', layerId, (e) => {
          clickHandled = true;

          const feature = e.features?.[0];
          if (!feature) return;

          const entityData = {
            type: getEntityType(layerId),
            id: feature.properties?.['id'],
            properties: feature.properties,
            coordinates: e.lngLat,
          };

          this.entityClick.emit(entityData);
        });
        // El cursor 'pointer' lo maneja EntityManagerRegistry con un único
        // mousemove (un par mouseenter/mouseleave por layer costaba una
        // consulta de features por layer en cada movimiento del mouse).
      }
    });

    // 👇 Click general del mapa
    this.map.on('click', (e) => {
      if (clickHandled) {
        clickHandled = false;
        return;
      }

      // Filtrar solo los layers que existen actualmente en el mapa
      const existingLayers = clickableLayers.filter((layerId) =>
        this.map?.getLayer(layerId),
      );

      if (existingLayers.length === 0) {
        this.entityClick.emit(null);
        return;
      }

      const features = this.map!.queryRenderedFeatures(e.point, {
        layers: existingLayers,
      });

      if (!features.length) {
        this.entityClick.emit(null);
      }
    });
  }
  // ========== MAP STYLE HANDLERS ==========

  //Maneja el cambio de estilo de mapa desde el selector
  onMapStyleChange(tipo: TipoMapaBase): void {
    this.style = tipo;
    this.changeStyle(tipo);
  }
}
